import { useState, useRef, useEffect } from "react";
import { ArrowLeft, Trash, CaretRight, Check, X, Barbell, DownloadSimple, PencilSimple, DotsThreeVertical, FloppyDisk } from "@phosphor-icons/react";
import { T } from "../../themes";
import { TODAY_ISO, fmtDate, fmtDuration } from "../../constants";
import {
  completedSets, totalVolume, exerciseVolumeSeries, fmtWeight,
  MuscleVolumeChart, VolumeBars, Tag,
  SectionLabel, Btn
} from "../../workoutHelpers";
import AppHeader from "../../components/AppHeader";

export default function HistoryScreen({sessions,library,unit,prMap,onDeleteSession,onUpdateSession,persona,darkMode,setDarkMode,profile}){
  const [selectedSession,setSelectedSession]=useState(null);
  const [selectedExercise,setSelectedExercise]=useState(null);
  const [confirmDelete,setConfirmDelete]=useState(null);
  const [prMuscle,setPrMuscle]=useState("All");
  const [editing,setEditing]=useState(false);
  const [editName,setEditName]=useState("");
  const [editExercises,setEditExercises]=useState([]);
  const [menuOpen,setMenuOpen]=useState(null);
  const menuRef=useRef(null);
  const sorted=[...sessions].sort((a,b)=>b.date<a.date?-1:1);
  const [prModal,setPrModal]=useState(null);

  // Close three-dot menu on outside click
  useEffect(()=>{
    if(!menuOpen) return;
    const handler=(e)=>{if(menuRef.current&&!menuRef.current.contains(e.target)) setMenuOpen(null);};
    document.addEventListener("mousedown",handler);
    return ()=>document.removeEventListener("mousedown",handler);
  },[menuOpen]);

  function startEditing(sess){
    setEditing(true);
    setEditName(sess.name||"Workout");
    setEditExercises(JSON.parse(JSON.stringify(sess.exercises||[])));
  }

  function cancelEditing(){
    setEditing(false);
    setEditName("");
    setEditExercises([]);
  }

  async function saveEditing(){
    if(!selectedSession||!onUpdateSession) return;
    const updates={name:editName,exercises:editExercises};
    await onUpdateSession(selectedSession.id,updates);
    setSelectedSession({...selectedSession,...updates});
    setEditing(false);
  }

  function updateSet(exIdx,setIdx,field,value){
    setEditExercises(prev=>{
      const next=[...prev];
      next[exIdx]={...next[exIdx],sets:[...next[exIdx].sets]};
      next[exIdx].sets[setIdx]={...next[exIdx].sets[setIdx],[field]:value};
      return next;
    });
  }

  const inputStyle={background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,color:T.inkDark,fontFamily:T.fontSans,fontWeight:600,fontSize:12,padding:"6px 8px",width:54,textAlign:"center"};


  /* ── Exercise detail view ──────────────────────────────────────────── */
  if(selectedExercise){
    const series=exerciseVolumeSeries(sessions,selectedExercise.id);
    const pr=prMap[selectedExercise.id];
    return(
      <div style={{background:T.pageBg,minHeight:"100dvh"}}>
        <div style={{padding:"14px 16px",display:"flex",alignItems:"center",gap:10,background:T.pageBg}}>
          <button onClick={()=>setSelectedExercise(null)} style={{width:32,height:32,borderRadius:"50%",background:T.surfaceLight,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <ArrowLeft size={16} weight="bold" color={T.inkMuted}/>
          </button>
          <span style={{fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark}}>{selectedExercise.name}</span>
        </div>
        <div style={{padding:"0 16px 8px"}}>
          {pr&&<div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16,marginBottom:10}}>
            <p style={{margin:"0 0 2px",fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans,textTransform:"uppercase",letterSpacing:"1px"}}>Personal record</p>
            <p style={{margin:0,fontSize:30,fontFamily:T.fontDisplay,fontWeight:800,color:T.gold}}>{fmtWeight(pr.maxWeight,unit)}</p>
            <p style={{margin:"4px 0 0",fontSize:10,fontFamily:T.fontSans,color:T.inkMuted}}>Set on {fmtDate(pr.date)}</p>
          </div>}
          <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16,marginBottom:10}}>
            <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans,textTransform:"uppercase",letterSpacing:"1px"}}>Volume trend (last 10)</p>
            <VolumeBars data={series}/>
          </div>
          <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16,marginBottom:10}}>
            <p style={{margin:"0 0 8px",fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans,textTransform:"uppercase",letterSpacing:"1px"}}>Session history</p>
            {sorted.map(sess=>{
              const ex=(sess.exercises||[]).find(e=>e.exerciseId===selectedExercise.id);
              if(!ex) return null;
              return(
                <div key={sess.id} style={{marginBottom:10,paddingBottom:10,borderBottom:`1px solid ${T.cardBorder}`}}>
                  <p style={{margin:"0 0 4px",fontSize:11,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted}}>{fmtDate(sess.date)}</p>
                  {ex.sets.map((s,si)=>(
                    <div key={si} style={{display:"flex",gap:12,fontSize:12,fontFamily:T.fontSans,color:s.done?T.inkDark:T.inkMuted,marginBottom:2}}>
                      <span style={{minWidth:40}}>Set {si+1}</span>
                      <span>{fmtWeight(s.weight,unit)}</span>
                      <span>{s.reps} reps</span>
                      {s.rpe?<span>RPE {s.rpe}</span>:null}
                      {!s.done&&<span style={{color:T.inkMuted}}>(skipped)</span>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  /* ── Session detail view ───────────────────────────────────────────── */
  if(selectedSession){
    const displayExercises=editing?editExercises:(selectedSession.exercises||[]);
    return(
      <div style={{background:T.pageBg,minHeight:"100dvh"}}>
        <div style={{padding:"14px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",background:T.pageBg}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <button onClick={()=>{cancelEditing();setSelectedSession(null);}} style={{width:32,height:32,borderRadius:"50%",background:T.surfaceLight,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <ArrowLeft size={16} weight="bold" color={T.inkMuted}/>
            </button>
            <div>
              {editing?(
                <input value={editName} onChange={e=>setEditName(e.target.value)} style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,color:T.inkDark,fontFamily:T.fontDisplay,fontSize:18,fontWeight:800,outline:"none",padding:"4px 8px",width:180}}/>
              ):(
                <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:18,fontWeight:800,color:T.inkDark}}>{selectedSession.name||"Workout"}</p>
              )}
              <p style={{margin:"2px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>{fmtDate(selectedSession.date)}</p>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {!editing&&<button onClick={()=>startEditing(selectedSession)} style={{background:"none",border:"none",color:T.primary,cursor:"pointer"}}><PencilSimple size={18} weight="bold"/></button>}
            {!editing&&<button onClick={()=>setConfirmDelete(selectedSession)} style={{background:"none",border:"none",color:T.red||"#e74c3c",cursor:"pointer"}}><Trash size={18} weight="bold"/></button>}
          </div>
        </div>
        <div style={{padding:"0 16px 8px"}}>
          {editing&&(
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              <Btn onClick={saveEditing} style={{flex:1}}><FloppyDisk size={14} weight="bold"/> Save changes</Btn>
              <Btn outline onClick={cancelEditing} style={{flex:1}}>Cancel</Btn>
            </div>
          )}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:14}}>
            {[{label:"Duration",value:fmtDuration(selectedSession.duration||0)},{label:"Volume",value:totalVolume(selectedSession,unit)},{label:"Sets done",value:completedSets(selectedSession)},{label:"Kcal burned",value:selectedSession.calories_burned>0?`~${selectedSession.calories_burned}`:"---"}].map(st=>(
              <div key={st.label} style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusSm,padding:10,textAlign:"center"}}>
                <p style={{margin:"0 0 2px",fontSize:9,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px"}}>{st.label}</p>
                <p style={{margin:0,fontSize:16,fontFamily:T.fontDisplay,fontWeight:800,color:T.inkDark}}>{st.value}</p>
              </div>
            ))}
          </div>
          {selectedSession.notes&&(
            <div style={{background:T.card,borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder}`,padding:"12px 14px",marginBottom:10}}>
              <p style={{margin:"0 0 4px",fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans,textTransform:"uppercase",letterSpacing:"1px"}}>Session notes</p>
              <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,color:T.inkMuted,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{selectedSession.notes}</p>
            </div>
          )}
          {displayExercises.map((ex,ei)=>(
            <div key={ei} style={{background:T.card,borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder}`,padding:14,marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:16,fontWeight:700,color:T.inkDark}}>{ex.name}</p>
                {!editing&&(
                  <button onClick={()=>{const lx=library.find(e=>e.id===ex.exerciseId);if(lx)setSelectedExercise(lx);}} style={{background:"none",border:"none",cursor:"pointer",color:T.primary,fontSize:10,fontFamily:T.fontSans,fontWeight:700}}>
                    Progress
                  </button>
                )}
              </div>
              {editing?(
                <div>
                  <div style={{display:"flex",gap:8,marginBottom:6,fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,textTransform:"uppercase",letterSpacing:"1px"}}>
                    <span style={{minWidth:42}}>Set</span>
                    <span style={{width:54,textAlign:"center"}}>Weight</span>
                    <span style={{width:54,textAlign:"center"}}>Reps</span>
                    <span style={{width:54,textAlign:"center"}}>RPE</span>
                  </div>
                  {ex.sets.map((s,si)=>(
                    <div key={si} style={{display:"flex",gap:8,marginBottom:4,alignItems:"center"}}>
                      <span style={{minWidth:42,fontSize:12,fontFamily:T.fontSans,color:T.inkMuted}}>Set {si+1}</span>
                      <input inputMode="decimal" value={s.weight||""} onChange={e=>updateSet(ei,si,"weight",e.target.value===""?"":parseFloat(e.target.value)||0)} style={inputStyle}/>
                      <input inputMode="numeric" value={s.reps||""} onChange={e=>updateSet(ei,si,"reps",e.target.value===""?"":parseInt(e.target.value)||0)} style={inputStyle}/>
                      <input inputMode="decimal" value={s.rpe||""} onChange={e=>{const v=e.target.value;updateSet(ei,si,"rpe",v===""?"":parseFloat(v)||0);}} style={inputStyle} placeholder="-"/>
                    </div>
                  ))}
                </div>
              ):(
                ex.sets.map((s,si)=>(
                  <div key={si} style={{display:"flex",gap:10,fontSize:12,fontFamily:T.fontSans,color:s.done?T.inkDark:T.inkMuted,marginBottom:3,alignItems:"center"}}>
                    <span style={{minWidth:42}}>Set {si+1}</span>
                    {s.setType&&s.setType!=="normal"&&(
                      <span style={{fontSize:9,fontFamily:T.fontSans,fontWeight:700,padding:"2px 6px",borderRadius:6,
                        background:s.setType==="warmup"?T.yellow:s.setType==="drop"?T.secondary:T.red,
                        color:T.ink}}>
                        {s.setType}
                      </span>
                    )}
                    <span style={{fontWeight:600}}>{fmtWeight(s.weight||0,unit)}</span>
                    <span>{s.reps} reps</span>
                    {s.rpe?<span>RPE {s.rpe}</span>:null}
                    {s.done?<Check size={12} weight="bold" color={T.primary}/>:<X size={12} weight="bold" color={T.inkMuted}/>}
                  </div>
                ))
              )}
            </div>
          ))}
          {editing&&(
            <div style={{display:"flex",gap:8,marginTop:14}}>
              <Btn onClick={saveEditing} style={{flex:1}}><FloppyDisk size={14} weight="bold"/> Save changes</Btn>
              <Btn outline onClick={cancelEditing} style={{flex:1}}>Cancel</Btn>
            </div>
          )}
        </div>
        {confirmDelete&&(
          <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
            <div style={{background:T.card,borderRadius:18,padding:24,maxWidth:300,width:"90%"}}>
              <p style={{margin:"0 0 8px",fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark}}>Delete session?</p>
              <p style={{margin:"0 0 16px",fontSize:12,fontFamily:T.fontSans,color:T.inkMuted}}>This cannot be undone.</p>
              <Btn danger onClick={()=>{onDeleteSession(confirmDelete.id);setConfirmDelete(null);setSelectedSession(null);}} style={{marginBottom:8}}>Yes, delete</Btn>
              <Btn outline onClick={()=>setConfirmDelete(null)}>Cancel</Btn>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ── Session list view (main) ──────────────────────────────────────── */
  return(
    <div style={{background:T.pageBg,minHeight:"100dvh"}}>
      <AppHeader
        title="History"
        subtitle={`${sessions.length} session${sessions.length!==1?"s":""}`}
        currentApp="lift"
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        username={profile?.username}
        rightContent={sessions.length > 0 ? (
          <button onClick={() => {
            const headers = ["Date","Workout Name","Duration (mins)","Exercises","Total Volume (kg)","Calories Burned"];
            const rows = sorted.map(s => {
              const exercises = (s.exercises || []).map(e => e.name).join(" | ");
              const volume = (s.exercises || []).reduce((sum, e) =>
                sum + (e.sets || []).reduce((s2, st) =>
                  s2 + ((parseFloat(st.weight) || 0) * (parseInt(st.reps) || 0)), 0), 0);
              return [
                s.date || "",
                s.name || "Workout",
                s.duration ? Math.round(s.duration / 60) : "",
                exercises,
                Math.round(volume),
                s.calories_burned || "",
              ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(",");
            });
            const csv = [headers.join(","), ...rows].join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `liftlithe-history-${TODAY_ISO()}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }} style={{background:T.surfaceLight,border:"none",borderRadius:20,padding:"5px 12px",cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontSize:10,fontWeight:700,color:T.inkMuted,fontFamily:T.fontSans}}>
            <DownloadSimple size={12} weight="bold"/> CSV
          </button>
        ) : null}
      />

      <div style={{padding:"0 16px 8px"}}>
        <MuscleVolumeChart sessions={sessions} library={library}/>
        {/* V26 FIX 20: Session volume chart */}
        {sessions.length >= 2 && (()=>{
          const last30 = sorted.slice(0, 30).reverse(); // oldest first
          const volData = last30.map(s => {
            let vol = 0;
            for (const ex of (s.exercises||[])) for (const st of (ex.sets||[])) if (st.done) vol += (st.weight||0) * (st.reps||0);
            return { date: s.date, vol: unit === "lbs" ? Math.round(vol * 2.20462) : Math.round(vol) };
          });
          const maxVol = Math.max(...volData.map(d => d.vol), 1);
          // 7-session rolling average
          const rollingAvg = volData.map((d, i) => {
            const window = volData.slice(Math.max(0, i - 6), i + 1);
            return { date: d.date, avg: Math.round(window.reduce((s, w) => s + w.vol, 0) / window.length) };
          });
          const maxAll = Math.max(maxVol, ...rollingAvg.map(d => d.avg), 1);
          const H = 80;
          return (
            <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,marginBottom:10,overflow:"hidden"}}>
              <div style={{background:T.tileBg,borderRadius:"18px 18px 0 0",padding:"14px 18px",display:"flex",alignItems:"center",gap:10,borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                <span style={{fontSize:20}}>📊</span>
                <div>
                  <div style={{color:T.tileAccent,fontWeight:700,fontSize:15,fontFamily:T.fontDisplay}}>Volume per session</div>
                  <div style={{color:T.inkFaint,fontSize:12}}>kg lifted · 7-session average</div>
                </div>
              </div>
              <div style={{padding:16}}>
              <div style={{display:"flex",gap:3,alignItems:"flex-end",height:H,marginBottom:0}}>
                {volData.map((d, i) => {
                  const pct = d.vol / maxAll;
                  return (
                    <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                      <div style={{
                        width:"100%",
                        height: `${Math.max(pct * (H - 20), 2)}px`,
                        background: i === volData.length - 1 ? T.accent : T.inkFaint,
                        borderRadius: "2px 2px 0 0",
                      }}/>
                      {volData.length <= 10 && (
                        <span style={{fontSize:6,color:T.inkMuted,fontFamily:T.fontSans}}>{d.date.slice(5)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Rolling average line overlay */}
              {volData.length >= 3 && (
                <svg width="100%" height={36} viewBox={`0 0 ${volData.length * 12} 36`} style={{display:"block",overflow:"hidden",marginTop:0,marginBottom:4}}>
                  <polyline
                    fill="none" stroke={T.tileAccent||T.primary} strokeWidth={2} strokeLinejoin="round"
                    points={rollingAvg.map((d, i) => `${i * 12 + 6},${36 - (d.avg / maxAll) * 32}`).join(" ")}
                  />
                </svg>
              )}
              <p style={{color:T.inkMuted,fontSize:9,fontFamily:T.fontSans,fontWeight:600,margin:0}}>
                Last {volData.length} sessions · {unit === "lbs" ? "lbs" : "kg"} volume · line = 7-session avg
              </p>
              </div>
            </div>
          );
        })()}
        {Object.keys(prMap).length>0&&(()=>{
          const allMuscles=["All",...new Set(Object.keys(prMap).flatMap(id=>{const lx=library.find(e=>e.id===id);return lx?(lx.muscles||[]):[];}))].sort();
          const filtered=Object.entries(prMap).filter(([exId])=>{
            if(prMuscle==="All") return true;
            const lx=library.find(e=>e.id===exId);
            return lx&&(lx.muscles||[]).includes(prMuscle);
          });
          return(
            <div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,marginBottom:10,overflow:"hidden"}}>
              <div style={{background:T.tileBg,borderRadius:"18px 18px 0 0",padding:"14px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <span style={{fontSize:20}}>🏅</span>
                  <div>
                    <div style={{color:T.tileAccent,fontWeight:700,fontSize:15,fontFamily:T.fontDisplay}}>Personal records</div>
                    <div style={{color:T.inkFaint,fontSize:12}}>Best lift per exercise</div>
                  </div>
                </div>
                <select value={prMuscle} onChange={e=>setPrMuscle(e.target.value)}
                  style={{background:T.surfaceMid,border:`1px solid ${T.cardBorder}`,borderRadius:20,color:T.inkDark,fontFamily:T.fontSans,fontSize:13,padding:"4px 12px",cursor:"pointer"}}>
                  {allMuscles.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{padding:16}}>
              {filtered.length===0&&<p style={{margin:0,fontSize:11,fontFamily:T.fontSans,color:T.inkMuted}}>No PRs for this muscle group yet.</p>}
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {filtered.map(([exId,pr])=>{
                  const lx=library.find(e=>e.id===exId);
                  if(!lx) return null;
                  return(
                    <button key={exId} onClick={()=>setPrModal({exId,lx,pr})} style={{background:T.surfaceMid,borderRadius:12,padding:"10px 12px",minWidth:100,cursor:"pointer",textAlign:"left",border:"none"}}>
                      <p style={{margin:0,fontSize:12,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted}}>{lx.name}</p>
                      <p style={{margin:"2px 0 0",fontSize:16,fontFamily:T.fontDisplay,fontWeight:700,color:T.accent}}>{fmtWeight(pr.maxWeight,unit)}</p>
                    </button>
                  );
                })}
              </div>
              </div>
            </div>
          );
        })()}
        {sorted.length===0&&<div style={{background:T.card,borderRadius:18,border:`1px solid ${T.cardBorder}`,padding:16}}><div style={{textAlign:"center",padding:"20px 0",color:T.inkMuted}}><Barbell size={32} weight="bold" style={{margin:"0 auto 10px"}}/><p style={{fontSize:14,fontFamily:T.fontDisplay,fontWeight:800}}>No sessions logged</p></div></div>}
        {sorted.length>0&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 4px",marginBottom:8}}>
            <span style={{color:T.inkMuted,fontSize:13,fontWeight:600,fontFamily:T.fontSans}}>Recent sessions</span>
            <span style={{color:T.inkFaint,fontSize:12}}>{sorted.length} sessions</span>
          </div>
        )}
        {sorted.map(sess=>(
          <div key={sess.id} style={{position:"relative",marginBottom:8}}>
            <button onClick={()=>setSelectedSession(sess)} style={{display:"block",width:"100%",textAlign:"left",background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:"14px 16px",cursor:"pointer"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:18,fontWeight:800,color:T.inkDark}}>{sess.name||"Workout"}</p>
                  <p style={{margin:"3px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted,fontWeight:500}}>{fmtDate(sess.date)} · {(sess.exercises||[]).length} exercises · {completedSets(sess)} sets · {totalVolume(sess,unit)}</p>
                  <div style={{display:"flex",flexWrap:"wrap",marginTop:6}}>
                    {(sess.exercises||[]).slice(0,4).map((ex,i)=><Tag key={i}>{ex.name}</Tag>)}
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0,paddingLeft:8}}>
                  <p style={{margin:0,fontSize:11,fontFamily:T.fontSans,color:T.inkMuted}}>{fmtDuration(sess.duration||0)}</p>
                  <CaretRight size={14} weight="bold" color={T.inkMuted} style={{marginTop:4}}/>
                </div>
              </div>
            </button>
            {/* Three-dot menu */}
            <div style={{position:"absolute",top:12,right:12,zIndex:10}}>
              <button onClick={(e)=>{e.stopPropagation();setMenuOpen(menuOpen===sess.id?null:sess.id);}} style={{background:"none",border:"none",cursor:"pointer",color:T.inkMuted,padding:4}}>
                <DotsThreeVertical size={18} weight="bold"/>
              </button>
              {menuOpen===sess.id&&(
                <div ref={menuRef} style={{position:"absolute",right:0,top:"100%",background:T.card,border:`1px solid ${T.cardBorder}`,boxShadow:T.shadowSm,borderRadius:12,zIndex:20,minWidth:120,overflow:"hidden"}}>
                  <button onClick={(e)=>{e.stopPropagation();setMenuOpen(null);setSelectedSession(sess);}} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"12px 16px",cursor:"pointer",fontSize:13,fontFamily:T.fontSans,fontWeight:600,color:T.inkDark,borderBottom:`1px solid ${T.cardBorder}`}}>
                    View
                  </button>
                  <button onClick={(e)=>{e.stopPropagation();setMenuOpen(null);setSelectedSession(sess);startEditing(sess);}} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"12px 16px",cursor:"pointer",fontSize:13,fontFamily:T.fontSans,fontWeight:600,color:T.primary,borderBottom:`1px solid ${T.cardBorder}`}}>
                    Edit
                  </button>
                  <button onClick={(e)=>{e.stopPropagation();setMenuOpen(null);setConfirmDelete(sess);}} style={{display:"block",width:"100%",textAlign:"left",background:"none",border:"none",padding:"12px 16px",cursor:"pointer",fontSize:13,fontFamily:T.fontSans,fontWeight:600,color:T.red||"#e74c3c"}}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {prModal&&(
        <>
          <div onClick={()=>setPrModal(null)} style={{position:"fixed",inset:0,background:T.backdrop,zIndex:9999}}/>
          <div style={{position:"fixed",top:"10%",left:"5%",right:"5%",minHeight:200,maxHeight:"70vh",background:T.card,borderRadius:T.radius,zIndex:10000,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Header — fixed height */}
            <div style={{padding:"20px 20px 16px",borderBottom:`1px solid ${T.cardBorder}`,flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <span style={{fontSize:22}}>🏅</span>
                <div style={{flex:1}}>
                  <p style={{margin:0,fontFamily:T.fontDisplay,fontSize:18,fontWeight:800,color:T.inkDark}}>{prModal.lx.name}</p>
                  <p style={{margin:"2px 0 0",fontSize:11,fontFamily:T.fontSans,color:T.inkMuted}}>Best: {fmtWeight(prModal.pr.maxWeight,unit)}{prModal.pr.date?` · ${fmtDate(prModal.pr.date)}`:""}</p>
                </div>
                <button onClick={()=>setPrModal(null)} style={{background:"transparent",border:"none",padding:"4px 8px",cursor:"pointer",fontSize:20,color:T.inkMuted,lineHeight:1,flexShrink:0}}>✕</button>
              </div>
            </div>
            {/* Scrollable rows */}
            <div style={{flex:1,overflowY:"auto",padding:"0 16px"}}>
              {(()=>{
                const byDate={};
                sessions.forEach(sess=>{
                  (sess.exercises||[]).forEach(ex=>{
                    if((ex.exerciseId||ex.id)===prModal.exId){
                      (ex.sets||[]).filter(s=>s.done&&parseFloat(s.weight)>0).forEach(s=>{
                        const w=parseFloat(s.weight),r=parseInt(s.reps)||0;
                        if(!byDate[sess.date]||w>byDate[sess.date].weight||(w===byDate[sess.date].weight&&r>byDate[sess.date].reps)){
                          byDate[sess.date]={weight:w,reps:r};
                        }
                      });
                    }
                  });
                });
                const rows=Object.entries(byDate).map(([date,{weight,reps}])=>({date,weight,reps})).sort((a,b)=>b.weight-a.weight);
                if(rows.length===0) return <p style={{color:T.inkMuted,fontSize:12,fontFamily:T.fontSans,textAlign:"center",padding:"24px 0"}}>No logged sets found.</p>;
                // Stats
                const sessionCount=rows.length;
                const avgWeight=rows.length>0?(rows.reduce((s,r)=>s+r.weight,0)/rows.length).toFixed(1):0;
                const sortedByDate=[...rows].sort((a,b)=>a.date.localeCompare(b.date));
                const dateRange=sortedByDate.length>1?`${fmtDate(sortedByDate[0].date)} – ${fmtDate(sortedByDate[sortedByDate.length-1].date)}`:fmtDate(sortedByDate[0]?.date||"");
                return (
                  <>
                    {/* Stats row */}
                    <div style={{display:"flex",gap:8,padding:"12px 0 4px"}}>
                      {[
                        {value:sessionCount,label:"Sessions"},
                        {value:`${fmtWeight(parseFloat(avgWeight),unit)}`,label:"Avg weight"},
                        {value:dateRange,label:"Date range"},
                      ].map(({value,label})=>(
                        <div key={label} style={{flex:1,background:T.surfaceLight,borderRadius:T.radiusSm,padding:"8px 10px"}}>
                          <div style={{fontSize:11,fontWeight:600,color:T.inkDark,fontFamily:T.fontSans,lineHeight:1.2}}>{value}</div>
                          <div style={{fontSize:10,fontWeight:500,color:T.inkMuted,fontFamily:T.fontSans,marginTop:3}}>{label}</div>
                        </div>
                      ))}
                    </div>
                    {/* Session rows */}
                    {rows.slice(0,12).map((r,i)=>(
                      <div key={i} style={{
                        display:"flex",justifyContent:"space-between",alignItems:"center",
                        padding:i===rows.slice(0,12).length-1?"12px 0 24px":"12px 0",
                        borderTop:`1px solid ${T.cardBorder}`,
                        background:i===0?`${T.accent}12`:undefined,
                        marginLeft:i===0?-16:0,marginRight:i===0?-16:0,paddingLeft:i===0?16:0,paddingRight:i===0?16:0,
                      }}>
                        <span style={{fontSize:12,fontFamily:T.fontSans,fontWeight:500,color:T.inkMuted}}>{fmtDate(r.date)}</span>
                        <span style={{display:"flex",alignItems:"center",gap:5,fontSize:14,fontFamily:T.fontDisplay,fontWeight:i===0?700:500,color:i===0?T.accent:T.text}}>
                          {i===0&&<span style={{fontSize:13}}>🏆</span>}
                          {fmtWeight(r.weight,unit)} × {r.reps}
                        </span>
                      </div>
                    ))}
                    {rows.length===1&&(
                      <p style={{fontSize:12,fontFamily:T.fontSans,color:T.textDim,textAlign:"center",padding:"4px 0 16px",fontStyle:"italic"}}>Keep training to track your progression</p>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}
      {confirmDelete&&(
        <div style={{position:"fixed",inset:0,background:T.backdrop,display:"flex",alignItems:"center",justifyContent:"center",zIndex:100}}>
          <div style={{background:T.card,borderRadius:18,padding:24,maxWidth:300,width:"90%"}}>
            <p style={{margin:"0 0 8px",fontFamily:T.fontDisplay,fontSize:20,fontWeight:800,color:T.inkDark}}>Delete session?</p>
            <p style={{margin:"0 0 16px",fontSize:12,fontFamily:T.fontSans,color:T.inkMuted}}>This cannot be undone.</p>
            <Btn danger onClick={()=>{onDeleteSession(confirmDelete.id);setConfirmDelete(null);}} style={{marginBottom:8}}>Yes, delete</Btn>
            <Btn outline onClick={()=>setConfirmDelete(null)}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
}
